import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { getRecipe, listIngredients, listSteps } from '../graphql/queries';
import { updateRecipe, deleteRecipe, deleteIngredient, deleteStep } from '../graphql/mutations';

const client = generateClient();

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [attrs, recipeRes, ingRes, stepRes] = await Promise.all([
          fetchUserAttributes(),
          client.graphql({ query: getRecipe, variables: { id } }),
          client.graphql({ query: listIngredients, variables: { filter: { recipeId: { eq: id } }, limit: 100 } }),
          client.graphql({ query: listSteps, variables: { filter: { recipeId: { eq: id } }, limit: 100 } }),
        ]);

        const r = recipeRes.data.getRecipe;
        setRecipe(r);
        const currentUser = await getCurrentUser();
        const ownerVal = `${attrs.sub}::${currentUser.username}`;
        // 新形式(sub::username)と旧形式(usernameのみ)の両方に対応
        setIsOwner(r.owner === ownerVal || r.owner === currentUser.username || r.owner === attrs.sub);

        const ings = ingRes.data.listIngredients.items;
        const stps = stepRes.data.listSteps.items.sort((a, b) => a.order - b.order);
        setIngredients(ings);
        setSteps(stps);

        if (r.imageUrl) {
          const urlResult = await getUrl({ key: r.imageUrl });
          setImageUrl(urlResult.url.toString());
        }
      } catch (err) {
        console.error('読み込みエラー:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  const togglePublic = async () => {
    if (!isOwner) return;
    setSaving(true);
    try {
      const updated = await client.graphql({
        query: updateRecipe,
        variables: { input: { id, isPublic: !recipe.isPublic } }
      });
      setRecipe(updated.data.updateRecipe);
    } catch (err) {
      console.error('更新エラー:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('このレシピを削除しますか？')) return;
    try {
      for (const ing of ingredients) await client.graphql({ query: deleteIngredient, variables: { input: { id: ing.id } } });
      for (const step of steps) await client.graphql({ query: deleteStep, variables: { input: { id: step.id } } });
      await client.graphql({ query: deleteRecipe, variables: { input: { id } } });
      navigate('/timeline');
    } catch (err) {
      console.error('削除エラー:', err);
    }
  };

  if (loading) return <div style={{ padding: '100px 20px', textAlign: 'center', color: '#95a5a6' }}>読み込み中...</div>;
  if (!recipe) return <div style={{ padding: '100px 20px', textAlign: 'center', color: '#95a5a6' }}>レシピが見つかりません</div>;

  return (
    <div style={S.page}>
      <div style={S.container}>

        {/* 画像 */}
        {imageUrl && <img src={imageUrl} alt={recipe.title} style={S.heroImage} />}

        {/* タイトル・公開設定 */}
        <div style={S.header}>
          <h1 style={S.title}>{recipe.title}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span style={{ ...S.badge, background: recipe.isPublic ? '#27ae60' : '#95a5a6' }}>
              {recipe.isPublic ? '🌐 公開中' : '🔒 非公開'}
            </span>
            <span style={{ fontSize: '12px', color: '#95a5a6' }}>
              by {recipe.owner?.split('::')[0] || '匿名'}
            </span>
            {isOwner && (
              <button onClick={togglePublic} disabled={saving} style={S.toggleBtn}>
                {recipe.isPublic ? '非公開にする' : '公開する'}
              </button>
            )}
          </div>
        </div>

        {/* 説明 */}
        {recipe.description && (
          <div style={S.card}>
            <p style={{ margin: 0, lineHeight: '1.7', color: '#2c3e50' }}>{recipe.description}</p>
          </div>
        )}

        {/* 材料 */}
        <div style={S.card}>
          <h2 style={S.sectionTitle}>🥬 材料</h2>
          {ingredients.length === 0
            ? <p style={{ color: '#95a5a6' }}>材料の登録がありません</p>
            : ingredients.map(ing => (
              <div key={ing.id} style={S.ingredientRow}>
                <span style={{ fontWeight: 'bold' }}>{ing.name}</span>
                <span style={{ color: '#666' }}>{ing.amount}{ing.unit}</span>
              </div>
            ))
          }
        </div>

        {/* 手順 */}
        <div style={S.card}>
          <h2 style={S.sectionTitle}>📝 作り方</h2>
          {steps.length === 0
            ? <p style={{ color: '#95a5a6' }}>手順の登録がありません</p>
            : steps.map((step, i) => (
              <div key={step.id} style={S.stepRow}>
                <div style={S.stepNum}>{step.order}</div>
                <p style={{ margin: 0, lineHeight: '1.7', flex: 1 }}>{step.description}</p>
              </div>
            ))
          }
        </div>

        {/* オーナー操作 */}
        {isOwner && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button onClick={() => navigate(`/recipe/edit/${id}`)} style={{ ...S.actionBtn, background: '#2c3e50' }}>
              ✏️ 編集
            </button>
            <button onClick={handleDelete} style={{ ...S.actionBtn, background: '#e74c3c' }}>
              🗑️ 削除
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { padding: '80px 20px 40px', backgroundColor: '#f4f7f9', minHeight: '100vh' },
  container: { maxWidth: '600px', margin: '0 auto' },
  heroImage: { width: '100%', borderRadius: '16px', objectFit: 'cover', maxHeight: '280px', marginBottom: '20px' },
  header: { marginBottom: '16px' },
  title: { fontSize: '22px', fontWeight: '900', color: '#2c3e50', margin: 0 },
  badge: { padding: '4px 12px', borderRadius: '20px', color: '#fff', fontSize: '12px', fontWeight: 'bold' },
  toggleBtn: {
    padding: '4px 12px', borderRadius: '20px', border: '1px solid #ddd',
    background: '#fff', fontSize: '12px', cursor: 'pointer', color: '#555',
  },
  card: { background: '#fff', borderRadius: '16px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  sectionTitle: { fontSize: '16px', fontWeight: '900', color: '#2c3e50', marginTop: 0, marginBottom: '14px' },
  ingredientRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' },
  stepRow: { display: 'flex', gap: '14px', marginBottom: '16px', alignItems: 'flex-start' },
  stepNum: {
    width: '28px', height: '28px', borderRadius: '50%', background: '#e74c3c',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 'bold', fontSize: '13px', flexShrink: 0,
  },
  actionBtn: {
    flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
    color: '#fff', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer',
  },
};
